/**
 * DGUI (Declarative Generative UI) types.
 * Schema is produced by the agent/backend and consumed by the frontend component registry.
 */

/** Schema for a single DGUI component: component name + props. */
export interface DGUIComponentSchema {
  component: string;
  props: Record<string, unknown>;
}

/** One part of multipart message content: text or DGUI block. */
export type ContentPart =
  | { type: 'text'; text: string }
  | { type: 'dgui'; schema: DGUIComponentSchema };

/** Structured message content with parts (text + optional DGUI blocks). */
export interface DGUIMessageContent {
  parts: ContentPart[];
}

/** Type guard: content is structured with parts. */
export function isDGUIMessageContent(
  content: unknown
): content is DGUIMessageContent {
  return (
    typeof content === 'object' &&
    content !== null &&
    'parts' in content &&
    Array.isArray((content as DGUIMessageContent).parts)
  );
}

/** InfoCard props (align with Rithm card DGU data contract). */
export interface InfoCardProps {
  title?: string;
  subtitle?: string;
  items: Array<{ label: string; value: string }>;
  variant?: 'default' | 'compact';
}

/** Table DGUI props — tabular data (holdings, transactions, allocations). */
export interface TableDGUIProps {
  title?: string;
  subtitle?: string;
  columns: Array<{ id: string; label: string }>;
  rows: Array<Record<string, string | number>>;
  variant?: 'default' | 'compact' | 'striped';
  maxRows?: number;
}

/** List DGUI props — ordered or unordered list of items. */
export interface ListDGUIProps {
  title?: string;
  subtitle?: string;
  items: Array<string | { primary: string; secondary?: string }>;
  ordered?: boolean;
  variant?: 'default' | 'compact';
}

/** NeutralBar DGUI props — single-value indicator (status, metric). */
export interface NeutralBarDGUIProps {
  label: string;
  value: string;
  status?: 'neutral' | 'positive' | 'negative' | 'warning';
  subtitle?: string;
}

/** BarGraph DGUI props — bar chart (categories vs values, single or multi-series). */
export interface BarGraphDGUIProps {
  title?: string;
  subtitle?: string;
  categories: string[];
  series: Array<{ name: string; values: number[] }> | number[];
  orientation?: 'vertical' | 'horizontal';
  layout?: 'grouped' | 'stacked';
  colors?: string[];
  valueFormat?: 'number' | 'percent' | 'currency';
  currencyCode?: string;
}

/** LineGraph DGUI props — line chart (trends over ordered axis). */
export interface LineGraphDGUIProps {
  title?: string;
  subtitle?: string;
  labels: string[];
  series: Array<{ name: string; data: number[] }>;
  valueFormat?: 'number' | 'percent' | 'currency';
  currencyCode?: string;
  colors?: string[];
  showArea?: boolean;
}

/** PieChart DGUI props — pie/donut (composition, share of whole). */
export interface PieChartDGUIProps {
  title?: string;
  subtitle?: string;
  segments: Array<{ label: string; value: number }>;
  variant?: 'pie' | 'donut';
  showLabels?: boolean;
  labelFormat?: 'value' | 'percent';
  colors?: string[];
  valueFormat?: 'number' | 'percent' | 'currency';
  currencyCode?: string;
}

/** HeatMap DGUI props — 2D heat map (matrix with color scale). */
export interface HeatMapDGUIProps {
  title?: string;
  subtitle?: string;
  rowLabels: string[];
  columnLabels: string[];
  data: (number | null)[][];
  valueFormat?: 'number' | 'percent' | 'currency';
  currencyCode?: string;
  colorScale?: 'sequential' | 'diverging' | 'neutral';
  valueMin?: number;
  valueMax?: number;
  showCellValues?: boolean;
}
